import clsx from 'clsx';
import { Fullscreen, GripHorizontal, Maximize2, Minimize2, Monitor, MoveDiagonal2, PictureInPicture2, Shrink, X } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { useCallSession, type SharedScreen } from '../lib/call-session';
import { useI18n } from '../lib/i18n';
import { Button } from './ui';

/**
 * The admin's ScreenStage (apps/admin/src/components/ScreenStage.tsx), for the portal:
 * a screen someone shares in the call (2026-11-02: "add share screen feature in
 * calls"), big enough to read: the dock is far too small. It opens by itself,
 * large, when a share starts — under the top bar, over the sidebar, leaving the
 * dock its column on the end side of a wide window; on a narrower one, where the
 * dock sits over its top, with its own buttons at the bottom — and can be made
 * smaller (a corner on the start side, opposite the dock, past the sidebar), put
 * away to a button in that corner, or shown full screen. Two people sharing at once: one tab each, the newest shown.
 * It goes the moment the share stops — stopped by its owner, who hung up or
 * dropped, or the call ending.
 *
 * A window over the app (operator, 2026-11-02, a screenshot of it half under the
 * sidebar: "make the popup on top of app also let me move it"): over the sidebar
 * and the page — the sidebar no longer holds a layer of its own on a wide window
 * (Layout) — under the top bar and its menus, the dock, dialogs and toasts.
 * Dragged by its top or its picture, or moved by its grip's arrow keys; resized
 * by its corner, or that corner's arrow keys; a double-click on the grip (or its
 * Home key) puts it back. Its large and its smaller window each keep where they
 * were put, per browser (`useStageFrame`). Where the browser can, it also floats
 * over every other window — the browser's own picture-in-picture.
 *
 * Every call screen says the call is recorded; this one says what that means for
 * a screen: the call's sound is, the screen is not.
 */

type Size = 'large' | 'small' | 'away';

export function ScreenStage() {
  const session = useCallSession();
  const { t, localized } = useI18n();
  const screens = session.screens;
  const [size, setSize] = useState<Size>('large');
  const [chosen, setChosen] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  // Shown in the browser's own window, over every other: the page's is put away meanwhile.
  const [floating, setFloating] = useState(false);
  const stage = useRef<HTMLElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const shownHere = screens.length > 0 && size !== 'away' && !floating;
  const place = useStageFrame(size === 'small' ? 'small' : 'large', shownHere);

  // A share that has just begun is shown at once, large unless it was made smaller — though put away before.
  const known = useRef<string[]>([]);
  useEffect(() => {
    const fresh = screens.filter((s) => !known.current.includes(s.sid));
    known.current = screens.map((s) => s.sid);
    if (fresh.length === 0) return;
    setChosen(fresh[fresh.length - 1].sid);
    setSize((s) => (s === 'away' ? 'large' : s));
  }, [screens]);

  useEffect(() => {
    const watch = () => setFull(!!stage.current && document.fullscreenElement === stage.current);
    document.addEventListener('fullscreenchange', watch);
    return () => document.removeEventListener('fullscreenchange', watch);
  }, []);

  if (screens.length === 0) return null;
  const shown = screens.find((s) => s.sid === chosen) ?? screens[screens.length - 1];
  // Their name in the reader's language, from the call's people; LiveKit's own name otherwise.
  const nameOf = (s: SharedScreen) => {
    const p = session.current?.participants.find((x) => (x.user.kind === 'CLIENT' ? `client_${x.user.id}` : `user_${x.user.id}`) === s.identity);
    return p ? localized(p.user) : s.name;
  };

  const toggleFull = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void stage.current?.requestFullscreen().catch(() => undefined);
  };
  const float = () => void video.current?.requestPictureInPicture().catch(() => undefined);
  const showHere = () => {
    if (floating) void document.exitPictureInPicture().catch(() => undefined);
    setSize((s) => (s === 'away' ? 'large' : s));
  };
  const resizeLabel = size === 'large' ? t('calls.screen.smaller') : t('calls.screen.larger');
  const fullLabel = full ? t('calls.screen.exitFull') : t('calls.screen.full');
  const anchored = !place.style;

  return (
    <>
      {/* On a wide window, past the sidebar: the name card and its menu stay in reach. */}
      {!shownHere && (
        <div className="pointer-events-none fixed bottom-4 start-4 z-dropdown lg:start-[calc(theme(spacing.sidebar)+1rem)]">
          <Button className="pointer-events-auto shadow-lg" onClick={showHere}>
            <Monitor className="h-4 w-4" aria-hidden />
            {t('calls.screen.show', { name: nameOf(shown) })}
          </Button>
        </div>
      )}
      {/* Kept while put away or floating: the browser's own window shows this very picture. */}
      <section
        ref={stage}
        aria-label={t('calls.screen.label')}
        aria-hidden={!shownHere || undefined}
        style={place.style}
        onPointerDown={place.grab}
        className={clsx(
          // The dropdowns' layer, as the dock — and before it in the page, so the dock stays over it.
          'surface fixed z-dropdown touch-none select-none flex-col overflow-hidden rounded-lg shadow-lg ring-1 ring-[var(--border-subtle)]',
          // Put away, or shown in the browser's own window: out of the page, the picture still playing.
          shownHere ? 'flex' : 'hidden',
          full && '!rounded-none',
          place.moving && 'cursor-grabbing',
          anchored &&
            (size === 'large'
              ? 'inset-x-4 bottom-4 top-[calc(theme(spacing.topbar)+1rem)] max-lg:flex-col-reverse lg:end-[calc(min(23rem,100vw-2rem)+2rem)]'
              : 'bottom-4 start-4 w-[min(30rem,calc(100vw-2rem))] lg:start-[calc(theme(spacing.sidebar)+1rem)]'),
        )}
      >
        <header
          className={clsx(
            'relative flex cursor-grab flex-wrap items-center gap-x-3 gap-y-1 hairline px-3 pb-2 pt-3',
            anchored && size === 'large' ? 'max-lg:border-t max-lg:pe-9 lg:border-b' : 'border-b',
          )}
        >
          <StageGrip onKey={place.nudge} onPutBack={place.putBack} />
          <Monitor className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          {screens.length > 1 ? (
            <div role="tablist" aria-label={t('calls.screen.label')} className="flex min-w-0 flex-wrap gap-1">
              {screens.map((s) => (
                <button
                  key={s.sid}
                  type="button"
                  role="tab"
                  aria-selected={s.sid === shown.sid}
                  onClick={() => setChosen(s.sid)}
                  className={clsx(
                    'truncate rounded-sm px-2 py-1 text-body-sm',
                    s.sid === shown.sid ? 'bg-primary/15 font-medium text-[var(--text-primary)]' : 'text-muted hover:text-[var(--text-primary)]',
                  )}
                >
                  {t('calls.screen.of', { name: nameOf(s) })}
                </button>
              ))}
            </div>
          ) : (
            <p className="min-w-0 truncate text-body-sm font-medium text-[var(--text-primary)]">{t('calls.screen.of', { name: nameOf(shown) })}</p>
          )}
          <div className="ms-auto flex items-center gap-1">
            {canFloat() && (
              <Button size="sm" variant="ghost" onClick={float} title={t('calls.screen.float')} disabled={full}>
                <PictureInPicture2 className="h-4 w-4" aria-hidden />
                <span className="sr-only">{t('calls.screen.float')}</span>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSize(size === 'large' ? 'small' : 'large')} title={resizeLabel} disabled={full}>
              {size === 'large' ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
              <span className="sr-only">{resizeLabel}</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={toggleFull} aria-pressed={full} title={fullLabel}>
              {full ? <Shrink className="h-4 w-4" aria-hidden /> : <Fullscreen className="h-4 w-4" aria-hidden />}
              <span className="sr-only">{fullLabel}</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSize('away')} title={t('calls.screen.hide')} disabled={full}>
              <X className="h-4 w-4" aria-hidden />
              <span className="sr-only">{t('calls.screen.hide')}</span>
            </Button>
          </div>
          <p className="basis-full text-caption text-muted">{t('calls.screen.notRecorded')}</p>
        </header>
        <ScreenVideo
          key={shown.sid}
          screen={shown}
          videoRef={video}
          className={anchored && size === 'small' && !full ? 'aspect-video' : 'min-h-0 flex-1'}
          onOpen={toggleFull}
          onFloating={setFloating}
        />
        {!full && <StageCorner onGrab={place.resize} onKey={place.stretch} />}
      </section>
    </>
  );
}

/** One shared screen, playing: letterboxed, never cropped — what is shared is read. */
function ScreenVideo({
  screen,
  videoRef,
  className,
  onOpen,
  onFloating,
}: {
  screen: SharedScreen;
  videoRef: RefObject<HTMLVideoElement>;
  className: string;
  onOpen: () => void;
  onFloating: (on: boolean) => void;
}) {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const detach = screen.attach(el);
    const enter = () => onFloating(true);
    const leave = () => onFloating(false);
    el.addEventListener('enterpictureinpicture', enter);
    el.addEventListener('leavepictureinpicture', leave);
    return () => {
      el.removeEventListener('enterpictureinpicture', enter);
      el.removeEventListener('leavepictureinpicture', leave);
      // The share is over, or another is shown: its own window closes with it.
      if (document.pictureInPictureElement === el) void document.exitPictureInPicture().catch(() => undefined);
      onFloating(false);
      detach();
    };
  }, [screen, videoRef, onFloating]);
  return (
    <div className={clsx('relative bg-ink-deep', className)}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onPlaying={() => setPlaying(true)}
        onDoubleClick={onOpen}
        className="absolute inset-0 h-full w-full object-contain"
        data-screen={screen.identity}
      />
      {!playing && <p className="absolute inset-0 grid place-items-center text-body-sm text-neutral-400">{t('calls.screen.waiting')}</p>}
    </div>
  );
}

/** The browser can show a video in a window of its own, over every other (Chrome, Edge, Safari — not Firefox). */
function canFloat(): boolean {
  return typeof document !== 'undefined' && document.pictureInPictureEnabled === true && 'requestPictureInPicture' in HTMLVideoElement.prototype;
}

/** The handle on top of the window, as the dock's: dragged, or its arrow keys; a double-click puts it back. */
function StageGrip({ onKey, onPutBack }: { onKey: (e: ReactKeyboardEvent<HTMLButtonElement>) => void; onPutBack: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-stage-grip
      aria-label={t('calls.screen.move')}
      title={t('calls.screen.moveHint')}
      onKeyDown={onKey}
      onDoubleClick={onPutBack}
      className="absolute inset-x-0 top-0 mx-auto grid h-3 w-12 cursor-grab place-items-center rounded-sm text-faint hover:text-[var(--text-primary)] active:cursor-grabbing"
    >
      <GripHorizontal className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

/** The corner on the end side at the bottom: dragged, or its arrow keys, it makes the window bigger or smaller. */
function StageCorner({
  onGrab,
  onKey,
}: {
  onGrab: (e: ReactPointerEvent<HTMLButtonElement>) => void;
  onKey: (e: ReactKeyboardEvent<HTMLButtonElement>) => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-stage-corner
      aria-label={t('calls.screen.resize')}
      title={t('calls.screen.resizeHint')}
      onPointerDown={onGrab}
      onKeyDown={onKey}
      className="absolute bottom-0 end-0 grid h-6 w-6 cursor-nwse-resize place-items-center rounded-ss-md border-s border-t hairline bg-[var(--surface-card)] text-muted hover:text-[var(--text-primary)] rtl:cursor-nesw-resize"
    >
      <MoveDiagonal2 className="h-3.5 w-3.5 rtl:-scale-x-100" aria-hidden />
    </button>
  );
}

/** Where the window was put and how big — on screen, its top-left corner (`x`, `y`) and its size. */
interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The window's room: the page less its scroll bar, and where the top bar ends. */
interface Bounds {
  width: number;
  height: number;
  ceiling: number;
}

/** Each window's frame, kept per browser: null where it was never moved — or was put back. */
const FRAMES_KEY = 'viganium.screenStage';
/** Never nearer the window's edge, nor the top bar, than this — as the dock. */
const GAP = 16;
/** How far a press travels before it is a drag and not a click. */
const SLOP = 4;
/** One arrow key, and one with Shift. */
const STEP = 16;
const LEAP = 64;
/** Never smaller than this, where the window leaves room for it. */
const MIN_W = 320;
const MIN_H = 200;
const ARROWS: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
/** A press on these is theirs, not a grab: buttons other than the grip, the tabs. */
const NOT_A_GRAB = 'a, input, select, textarea, [role="tab"], button:not([data-stage-grip])';

/**
 * Moving the window and changing its size. Until it is moved it sits where it
 * opens (anchored by its classes, following the window's size); once moved or
 * resized it has a frame of its own, kept in this browser for the next share
 * and the next call, and brought back inside a window that has grown smaller.
 */
function useStageFrame(which: 'large' | 'small', open: boolean) {
  const [frames, setFrames] = useState<Record<'large' | 'small', Frame | null>>(readFrames);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [moving, setMoving] = useState(false);
  const release = useRef<(() => void) | null>(null);
  useEffect(() => () => release.current?.(), []);

  useEffect(() => {
    if (!open) return;
    const measure = () => setBounds(measureBounds());
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  const current = frames[which];
  const at = current && bounds ? fitted(current, bounds) : current;

  const set = (next: Frame | null, keep: boolean) => {
    setFrames((all) => {
      const updated = { ...all, [which]: next };
      if (keep) keepFrames(updated);
      return updated;
    });
  };

  /** A press and drag: moves the frame where it is now (`change`), every pointer move, until it is let go. */
  const follow = (e: ReactPointerEvent<HTMLElement>, change: (dx: number, dy: number, room: Bounds) => Frame) => {
    const room = measureBounds();
    const { pointerId, clientX: fromX, clientY: fromY } = e;
    let last: Frame | null = null;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (!last && Math.abs(ev.clientX - fromX) < SLOP && Math.abs(ev.clientY - fromY) < SLOP) return;
      if (!last) setMoving(true);
      last = change(ev.clientX - fromX, ev.clientY - fromY, room);
      set(last, false);
    };
    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      release.current?.();
      if (last) set(last, true);
    };
    // On the window, not the stage: a fast hand outruns it.
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    release.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      release.current = null;
      setMoving(false);
    };
  };

  const grab = (e: ReactPointerEvent<HTMLElement>) => {
    if (!e.isPrimary || e.button !== 0 || release.current || document.fullscreenElement) return;
    if ((e.target as Element).closest(NOT_A_GRAB)) return;
    const box = e.currentTarget.getBoundingClientRect();
    follow(e, (dx, dy, room) => placed({ x: box.left + dx, y: box.top + dy, w: box.width, h: box.height }, room));
  };

  const resize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!e.isPrimary || e.button !== 0 || release.current) return;
    e.stopPropagation();
    const section = e.currentTarget.closest('section');
    if (!section) return;
    const box = section.getBoundingClientRect();
    const rtl = getComputedStyle(section).direction === 'rtl';
    follow(e, (dx, dy, room) => stretched(box, rtl ? -dx : dx, dy, rtl, room));
  };

  const nudge = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const arrow = ARROWS[e.key];
    if (e.key === 'Home') {
      e.preventDefault();
      set(null, true);
    } else if (arrow) {
      e.preventDefault();
      const section = e.currentTarget.closest('section');
      if (!section) return;
      const box = section.getBoundingClientRect();
      const step = e.shiftKey ? LEAP : STEP;
      set(placed({ x: box.left + arrow[0] * step, y: box.top + arrow[1] * step, w: box.width, h: box.height }, measureBounds()), true);
    }
  };

  // The corner follows the arrow: away from the window's middle makes it bigger.
  const stretch = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const arrow = ARROWS[e.key];
    if (!arrow) return;
    e.preventDefault();
    const section = e.currentTarget.closest('section');
    if (!section) return;
    const box = section.getBoundingClientRect();
    const rtl = getComputedStyle(section).direction === 'rtl';
    const step = e.shiftKey ? LEAP : STEP;
    set(stretched(box, (rtl ? -arrow[0] : arrow[0]) * step, arrow[1] * step, rtl, measureBounds()), true);
  };

  const style: CSSProperties | undefined = at ? { left: at.x, top: at.y, width: at.w, height: at.h } : undefined;
  return { style, moving, grab, resize, nudge, stretch, putBack: () => set(null, true) };
}

/** A frame this size moved to (x, y), kept inside the window and below the top bar. */
function placed(f: Frame, room: Bounds): Frame {
  const w = clamp(f.w, Math.min(MIN_W, room.width - 2 * GAP), room.width - 2 * GAP);
  const h = clamp(f.h, Math.min(MIN_H, room.height - room.ceiling - 2 * GAP), room.height - room.ceiling - 2 * GAP);
  return {
    x: Math.round(clamp(f.x, GAP, room.width - w - GAP)),
    y: Math.round(clamp(f.y, room.ceiling + GAP, room.height - h - GAP)),
    w: Math.round(w),
    h: Math.round(h),
  };
}

/**
 * The window `box` made bigger by `grow` toward its corner on the end side (`rtl`: the left one) and by `down` at the
 * bottom — its other corner where it is, inside the window.
 */
function stretched(box: DOMRect, grow: number, down: number, rtl: boolean, room: Bounds): Frame {
  const minW = Math.min(MIN_W, room.width - 2 * GAP);
  const minH = Math.min(MIN_H, room.height - room.ceiling - 2 * GAP);
  const h = clamp(box.height + down, minH, room.height - GAP - box.top);
  if (rtl) {
    const w = clamp(box.width + grow, minW, box.right - GAP);
    return { x: Math.round(box.right - w), y: Math.round(box.top), w: Math.round(w), h: Math.round(h) };
  }
  const w = clamp(box.width + grow, minW, room.width - GAP - box.left);
  return { x: Math.round(box.left), y: Math.round(box.top), w: Math.round(w), h: Math.round(h) };
}

/** The same frame, brought inside the window should the window now be too small for it. */
function fitted(f: Frame, room: Bounds): Frame {
  const next = placed(f, room);
  return next.x === f.x && next.y === f.y && next.w === f.w && next.h === f.h ? f : next;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(n, max));
}

/** The box a fixed element is placed in — the window less its scroll bar — and where the top bar ends (as the dock's). */
function measureBounds(): Bounds {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;right:0;bottom:0;left:0;visibility:hidden;pointer-events:none';
  document.body.append(probe);
  const box = probe.getBoundingClientRect();
  probe.remove();
  const ceiling = Math.max(0, document.querySelector('[data-top-bar]')?.getBoundingClientRect().bottom ?? 0);
  return { width: box.width, height: box.height, ceiling };
}

function readFrames(): Record<'large' | 'small', Frame | null> {
  const valid = (f: unknown): f is Frame =>
    !!f && typeof f === 'object' && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite((f as Record<string, unknown>)[k]));
  try {
    const kept = JSON.parse(localStorage.getItem(FRAMES_KEY) ?? 'null') as Record<string, unknown> | null;
    const large = kept?.large;
    const small = kept?.small;
    return { large: valid(large) ? large : null, small: valid(small) ? small : null };
  } catch {
    return { large: null, small: null };
  }
}

function keepFrames(frames: Record<'large' | 'small', Frame | null>) {
  try {
    if (!frames.large && !frames.small) localStorage.removeItem(FRAMES_KEY);
    else localStorage.setItem(FRAMES_KEY, JSON.stringify(frames));
  } catch {
    // Storage refused (a private window, a full quota): it still moves, and forgets on a reload.
  }
}
